import { useEffect, useRef, useState } from 'react'
import { jobsApi } from '../api/client'
import { notifyJobFinished } from '../utils/notifications'
import type { JobStatusResponse } from '../types'

interface UseJobPollingOptions {
  interval?: number
  enabled?: boolean
  /** C5: fire a browser notification when the job reaches a terminal state. */
  notifyOnFinish?: boolean
  /** Stop polling after this many consecutive status errors. */
  maxConsecutiveErrors?: number
}

export function useJobPolling(
  jobId: string | null,
  options: UseJobPollingOptions = {}
) {
  const {
    interval = 2000,
    enabled = true,
    notifyOnFinish = true,
    maxConsecutiveErrors = 5,
  } = options
  const [status, setStatus] = useState<JobStatusResponse | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const intervalRef = useRef<number>()
  const notifiedRef = useRef(false)
  const consecutiveErrorsRef = useRef(0)

  useEffect(() => {
    if (!jobId || !enabled) {
      return
    }

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
    }

    const poll = async () => {
      try {
        const data = await jobsApi.getStatus(jobId)
        consecutiveErrorsRef.current = 0
        setStatus(data)
        setError(null)

        // Stop polling if job is done
        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          stopPolling()
          // C5: notify once when the job finishes.
          if (notifyOnFinish && !notifiedRef.current) {
            notifiedRef.current = true
            const ok = data.status === 'completed'
            void notifyJobFinished(
              ok ? '✅ PDF listo' : '❌ El trabajo falló',
              ok
                ? 'Tu PDF está listo para descargar.'
                : data.error || 'Revisa la página del trabajo para más detalles.',
              `/job/${jobId}`
            )
          }
        }
      } catch (err) {
        consecutiveErrorsRef.current += 1
        setError(err as Error)

        if (consecutiveErrorsRef.current >= maxConsecutiveErrors) {
          stopPolling()
        }
      }
    }

    // Initial fetch
    poll()

    // Start polling
    intervalRef.current = setInterval(poll, interval)

    return () => {
      stopPolling()
    }
  }, [jobId, interval, enabled, notifyOnFinish, maxConsecutiveErrors])

  return { status, error, isPolling: !!intervalRef.current }
}
